import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { useDocumentState } from '@/state'
import { cn, createSnapModifier } from '@/utils'
import type {
  FlattenedDocumentPage,
  TemplateAttributeTypes,
} from '@/lib/api/client'
import { useDocumentPageContextQuery } from '../../queries'
import {
  useAnnotationLayer,
  useSaveFreehandAnnotation,
  useSaveDraggableElement,
} from '../Annotations/queries'
import {
  Position,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Line } from '../Annotations/Line'
import {
  DEFAULT_STROKE_COLOR,
  DISABLED_STROKE_COLOR,
} from '../Annotations/constants'
import { isLine, isArrow } from '../Annotations/utils'
import DocumentGrid from '../DocumentGrid'
import { useListTemplateAttributes } from '@/services/queries/template_attributes'
import { AnnotationsProvider } from './AnnotationsContext'
import { DraggableElements } from '../DraggableElements'
import { useDeleteTemplateAttribute } from '@/services/queries/template_attributes'
import { KonvaAnnotationsLayer } from './KonvaAnnotationsLayer'
import { isKonvaAnnotationTemplate } from './annotationSchema'
import type {
  AnnotationToolPreset,
  KonvaAnnotationTemplate,
} from './annotationSchema'
import {
  getKonvaAnnotationDragDelta,
  getTemplateAttributeMoveUpdate,
} from '../templateAttributeGeometry'
import { getKonvaAnnotationStackBands } from '../templateAttributeStacking'
import { useVirtualizedKonvaAnnotations } from './useVirtualizedKonvaAnnotations'
import {
  dispatchKonvaAnnotationDragPreview,
  type KonvaAnnotationDragPreviewDetail,
} from './konvaAnnotationDragPreview'
import { getFreehandStrokeWidth } from './annotationPresets'
import { FREEHAND_CURSOR, isFreehandAnnotationType } from './annotationMetadata'

const GRID_HEIGHT = 20
const GRID_WIDTH = 20

type FreehandDrawingStroke = {
  points: Array<{ x: number; y: number }>
  stroke: string
  strokeWidth: number
} | null

/**
 * Backspace/Delete typed inside a rich-text editor should edit text,
 * not delete the entire selected annotation container.
 */
const isEditableKeyboardTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false

  return (
    Boolean(target.closest('.ProseMirror')) ||
    Boolean(target.isContentEditable) ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    (target.tagName === 'DIV' && target.getAttribute('role') === 'textbox')
  )
}

export const AnnotationsContainer = ({
  documentPage,
  fullSubPageIds: passedFullSubPageIds,
  hiddenSubPageIds: passedHiddenSubPageIds,
  isReadOnly,
  children,
}: {
  documentPage: FlattenedDocumentPage
  fullSubPageIds?: string[]
  hiddenSubPageIds?: Set<string>
  isReadOnly: boolean
  children?: ReactNode
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  const [freehandDrawingStroke, setFreehandDrawingStroke] =
    useState<FreehandDrawingStroke>(null)
  const [floatingOverlayElement, setFloatingOverlayElement] =
    useState<HTMLDivElement | null>(null)
  const { data: docData } = useDocumentPageContextQuery()
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })
  const annotationsCanvasRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const freehandDrawingStrokeRef = useRef<FreehandDrawingStroke>(null)
  const activeFreehandPointerIdRef = useRef<number | null>(null)
  const suppressNextAddClickRef = useRef(false)
  const removePendingAddClickSuppressorRef = useRef<(() => void) | null>(null)
  const pendingMultiSelectDeltaRef = useRef<{ x: number; y: number } | null>(
    null,
  )
  const multiSelectDeltaFrameRef = useRef<number | null>(null)
  const pendingKonvaAnnotationDragPreviewRef =
    useRef<KonvaAnnotationDragPreviewDetail | null>(null)
  const konvaAnnotationDragPreviewFrameRef = useRef<number | null>(null)
  const setLinePoint = useDocumentState((state) => state.setLinePoint)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const clearNewElementData = useDocumentState(
    (state) => state.clearNewElementData,
  )
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const { mutate: saveElement } = useSaveDraggableElement()
  const { mutateAsync: saveFreehandAnnotation } = useSaveFreehandAnnotation()
  const annotationLayerActions = useAnnotationLayer(documentPage.id as string)
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const setDocumentState = useDocumentState((state) => state.setState)
  const getDocumentState = useDocumentState((state) => state.getState)
  const setMultiSelectDelta = useDocumentState(
    (state) => state.setMultiSelectDelta,
  )
  const selectedAnnotationId = useDocumentState((state) =>
    state.selectedElement?.type === 'annotation'
      ? state.selectedElement.id
      : null,
  )
  const bulkSelectedTemplateAttributes = useDocumentState(
    (state) => state.bulkSelectedTemplateAttributes,
  )
  const multiSelectedElementIds = useMemo(
    () =>
      bulkSelectedTemplateAttributes.map(
        (templateAttribute) => templateAttribute.id as string,
      ),
    [bulkSelectedTemplateAttributes],
  )
  const currentPageInView = useDocumentState((state) => state.currentPageInView)
  const draggingElementId = useDocumentState((state) => state.draggingElementId)
  const setDraggingElementId = useDocumentState(
    (state) => state.setDraggingElementId,
  )
  const dataType = useMemo(
    () => newElementData?.dataType,
    [newElementData?.dataType],
  )
  const type = useMemo(
    () =>
      dataType === 'annotation' ? newElementData?.attributes.type : dataType,
    [dataType, newElementData?.attributes.type],
  )
  const isAddingFreehandAnnotation = useMemo(
    () => dataType === 'annotation' && isFreehandAnnotationType(type),
    [dataType, type],
  )
  const placementCursor = useMemo(() => {
    if (isAddingFreehandAnnotation) return FREEHAND_CURSOR
    if (type && isLine(type)) return 'crosshair'
    return 'auto'
  }, [isAddingFreehandAnnotation, type])
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage?.id as string,
  })

  const fullSubPageIds = useMemo(() => {
    // Use passed values if available (from OperationDocument), otherwise compute
    if (passedFullSubPageIds) return passedFullSubPageIds

    const subPages = documentPage?.template_values?.subPages as
      | Array<string>
      | undefined

    return subPages ?? (documentPage?.id ? [documentPage.id as string] : [])
  }, [passedFullSubPageIds, documentPage])

  const hiddenSubPageIds = useMemo(() => {
    // Use passed values if available (from OperationDocument), otherwise compute
    if (passedHiddenSubPageIds) return passedHiddenSubPageIds

    return new Set<string>(
      (documentPage?.template_values?.hiddenSubPages as string[] | undefined) ??
        [],
    )
  }, [passedHiddenSubPageIds, documentPage?.template_values?.hiddenSubPages])

  const subPageIds = useMemo(
    () => fullSubPageIds.filter((id) => !hiddenSubPageIds.has(id)),
    [fullSubPageIds, hiddenSubPageIds],
  )

  const printSubPageId = useMemo(
    () => documentPage?.template_values?.printSubPageId as string | undefined,
    [documentPage],
  )
  const printFirstSubPageId = useMemo(
    () =>
      documentPage?.template_values?.printFirstSubPageId as string | undefined,
    [documentPage],
  )
  const forceMountedTemplateAttributeIds = useMemo(() => {
    const ids = new Set<string>()

    if (selectedAnnotationId) {
      ids.add(selectedAnnotationId)
    }

    if (draggingElementId) {
      ids.add(draggingElementId)
    }

    bulkSelectedTemplateAttributes.forEach((templateAttribute) => {
      if (templateAttribute.id) {
        ids.add(templateAttribute.id as string)
      }
    })

    return ids
  }, [bulkSelectedTemplateAttributes, draggingElementId, selectedAnnotationId])
  const annotationTemplateAttributes = useMemo(
    () =>
      templateAttributes?.filter(
        (templateAttribute) => templateAttribute.data_type === 'annotation',
      ),
    [templateAttributes],
  )
  const { visibleAnnotationIds } = useVirtualizedKonvaAnnotations({
    annotationsContainerRef: annotationsCanvasRef,
    forceMountedIds: forceMountedTemplateAttributeIds,
    subPageIds,
    templateAttributes: annotationTemplateAttributes,
    printSubPageId,
    printFirstSubPageId,
    documentPageId: documentPage.id as string,
  })
  const konvaAnnotationStackBands = useMemo(
    () => getKonvaAnnotationStackBands(templateAttributes || []),
    [templateAttributes],
  )
  const visibleKonvaAnnotationStackBands = useMemo(
    () =>
      konvaAnnotationStackBands
        .map((band) => ({
          ...band,
          templateAttributes: band.templateAttributes.filter(
            (templateAttribute) =>
              visibleAnnotationIds.has(templateAttribute.id as string),
          ),
        }))
        .filter((band) => band.templateAttributes.length > 0),
    [konvaAnnotationStackBands, visibleAnnotationIds],
  )
  const {
    mutateAsync: deleteTemplateAttribute,
    isPending: isDeletingTemplateAttribute,
  } = useDeleteTemplateAttribute()
  const onRemove = useCallback(
    async (event: globalThis.KeyboardEvent) => {
      if (
        isReadOnly ||
        event.defaultPrevented ||
        isDeletingTemplateAttribute ||
        (event.key !== 'Delete' && event.key !== 'Backspace') ||
        isEditableKeyboardTarget(event.target)
      ) {
        return
      }

      if (
        currentPageInView === documentPage.id &&
        bulkSelectedTemplateAttributes.length
      ) {
        return
      }

      const selectedElement = getDocumentState().selectedElement

      if (
        !selectedElement?.isRemovable ||
        selectedElement.documentPageId !== documentPage.id
      ) {
        return
      }

      event.preventDefault()
      await deleteTemplateAttribute({
        documentPageId: selectedElement.documentPageId,
        templateAttributeId: selectedElement.id,
      })
      setDocumentState({ selectedElement: null })
    },
    [
      bulkSelectedTemplateAttributes,
      currentPageInView,
      deleteTemplateAttribute,
      documentPage.id,
      getDocumentState,
      isDeletingTemplateAttribute,
      isReadOnly,
      setDocumentState,
    ],
  )

  useEffect(() => {
    document.addEventListener('keydown', onRemove)
    return () => {
      document.removeEventListener('keydown', onRemove)
    }
  }, [onRemove])

  const addAnnotation = useCallback(
    (clickPosition: Position) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const iconPlaceholder = globalThis.document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const { width: placeholderWidth, height: placeholderHeight } =
        iconPlaceholder.getBoundingClientRect()
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      const canvasContentLeft =
        canvasRect.x + annotationsCanvasRef.current.clientLeft
      const canvasContentTop =
        canvasRect.y + annotationsCanvasRef.current.clientTop
      if (canDropElement) {
        const preset = newElementData.attributes.preset as
          | AnnotationToolPreset
          | undefined
        const isKonvaBoundsAnnotation =
          dataType === 'annotation' && preset?.defaultGeometry.mode === 'bounds'
        const annotationWidth = isKonvaBoundsAnnotation
          ? (preset.defaultGeometry.width ?? placeholderWidth / zoomPercentage)
          : placeholderWidth / zoomPercentage
        const annotationHeight = isKonvaBoundsAnnotation
          ? (preset.defaultGeometry.height ??
            placeholderHeight / zoomPercentage)
          : placeholderHeight / zoomPercentage

        saveElement({
          documentPage,
          position: {
            x:
              clickPosition.x / zoomPercentage -
              canvasContentLeft / zoomPercentage -
              annotationWidth / 2,
            y:
              clickPosition.y / zoomPercentage -
              canvasContentTop / zoomPercentage -
              annotationHeight / 2,
          },
        })
      }
    },
    [
      newElementData,
      type,
      canDropElement,
      dataType,
      zoomPercentage,
      saveElement,
      documentPage,
    ],
  )

  const addLine = useCallback(
    (clickPosition: { x: number; y: number }) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      if (!newElementData.attributes.startPoint && canDropElement) {
        setLinePoint({
          type: 'start',
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
      if (newElementData.attributes.startPoint && canDropElement) {
        saveElement({
          documentPage,
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
    },
    [
      documentPage,
      annotationsCanvasRef,
      newElementData,
      canDropElement,
      zoomPercentage,
      setLinePoint,
      saveElement,
    ],
  )

  const stopAddingElementEvent = useCallback(
    (event: MouseEvent | PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    },
    [],
  )

  const suppressNextNativeAddClick = useCallback(() => {
    removePendingAddClickSuppressorRef.current?.()

    const stopNextClick = (event: MouseEvent) => {
      if (
        !event.isTrusted ||
        !(event.target instanceof Node) ||
        !annotationsCanvasRef.current?.contains(event.target)
      ) {
        return
      }

      stopAddingElementEvent(event)
      removePendingAddClickSuppressorRef.current?.()
    }
    const removeSuppressor = () => {
      window.removeEventListener('click', stopNextClick, true)
      removePendingAddClickSuppressorRef.current = null
    }

    removePendingAddClickSuppressorRef.current = removeSuppressor
    window.addEventListener('click', stopNextClick, true)
    window.setTimeout(removeSuppressor, 100)
  }, [stopAddingElementEvent])

  const setFreehandDrawing = useCallback((stroke: FreehandDrawingStroke) => {
    freehandDrawingStrokeRef.current = stroke
    setFreehandDrawingStroke(stroke)
  }, [])

  const getFreehandDocumentPoint = useCallback(
    (event: PointerEvent) => {
      const canvasRect = annotationsCanvasRef.current?.getBoundingClientRect()

      if (!canvasRect) return null

      return {
        x: event.clientX / zoomPercentage - canvasRect.x / zoomPercentage,
        y: event.clientY / zoomPercentage - canvasRect.y / zoomPercentage,
      }
    },
    [zoomPercentage],
  )

  const stopFreehandDrawing = useCallback(() => {
    activeFreehandPointerIdRef.current = null
    setFreehandDrawing(null)
  }, [setFreehandDrawing])

  const hasFreehandDrawingDistance = useCallback(
    (points: Array<{ x: number; y: number }>) => {
      const [startPoint] = points
      if (!startPoint) return false

      return points.some(
        (point) =>
          Math.hypot(point.x - startPoint.x, point.y - startPoint.y) >= 1,
      )
    },
    [],
  )

  const onFreehandPointerDown = useCallback(
    (event: PointerEvent) => {
      if (
        !newElementData ||
        !isAddingFreehandAnnotation ||
        (event.pointerType === 'mouse' && event.button !== 0) ||
        !(event.target instanceof Node) ||
        !annotationsCanvasRef.current?.contains(event.target)
      ) {
        return
      }

      const point = getFreehandDocumentPoint(event)
      if (!point) return

      stopAddingElementEvent(event)
      suppressNextNativeAddClick()
      activeFreehandPointerIdRef.current = event.pointerId

      const preset = newElementData.attributes.preset as
        | AnnotationToolPreset
        | undefined
      const stroke =
        newElementData.attributes.strokeColor || DEFAULT_STROKE_COLOR
      const strokeWidth =
        preset?.defaultStyle.strokeWidth ??
        getFreehandStrokeWidth(newElementData.attributes.type)

      setFreehandDrawing({
        points: [point, point],
        stroke,
        strokeWidth,
      })
    },
    [
      getFreehandDocumentPoint,
      isAddingFreehandAnnotation,
      newElementData,
      setFreehandDrawing,
      stopAddingElementEvent,
      suppressNextNativeAddClick,
    ],
  )

  const onFreehandPointerMove = useCallback(
    (event: PointerEvent) => {
      if (activeFreehandPointerIdRef.current !== event.pointerId) return

      const point = getFreehandDocumentPoint(event)
      const stroke = freehandDrawingStrokeRef.current
      if (!point || !stroke) return

      event.preventDefault()
      setFreehandDrawing({
        ...stroke,
        points: [...stroke.points, point],
      })
    },
    [getFreehandDocumentPoint, setFreehandDrawing],
  )

  const onFreehandPointerUp = useCallback(
    (event: PointerEvent) => {
      if (activeFreehandPointerIdRef.current !== event.pointerId) return

      stopAddingElementEvent(event)

      const stroke = freehandDrawingStrokeRef.current
      const endPoint = getFreehandDocumentPoint(event)
      const points =
        endPoint && stroke ? [...stroke.points, endPoint] : stroke?.points
      stopFreehandDrawing()

      if (!points || !hasFreehandDrawingDistance(points)) {
        clearNewElementData()
        return
      }

      void saveFreehandAnnotation({
        documentPage,
        points,
      })
    },
    [
      documentPage,
      getFreehandDocumentPoint,
      hasFreehandDrawingDistance,
      clearNewElementData,
      saveFreehandAnnotation,
      stopAddingElementEvent,
      stopFreehandDrawing,
    ],
  )

  const onFreehandPointerCancel = useCallback(
    (event: PointerEvent) => {
      if (activeFreehandPointerIdRef.current !== event.pointerId) return

      stopFreehandDrawing()
    },
    [stopFreehandDrawing],
  )

  const onAddAnnotation = useCallback(
    (event: MouseEvent | PointerEvent) => {
      if (!newElementData) return

      stopAddingElementEvent(event)

      if (isLine(type)) {
        addLine({ x: event.clientX, y: event.clientY })
      } else {
        addAnnotation({ x: event.clientX, y: event.clientY })
      }
    },
    [newElementData, type, addAnnotation, addLine, stopAddingElementEvent],
  )

  const onAddAnnotationPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      if (
        !(event.target instanceof Node) ||
        !annotationsCanvasRef.current?.contains(event.target)
      ) {
        return
      }

      suppressNextAddClickRef.current = true
      suppressNextNativeAddClick()
      onAddAnnotation(event)
    },
    [onAddAnnotation, suppressNextNativeAddClick],
  )

  const onAddAnnotationClick = useCallback(
    (event: MouseEvent) => {
      if (suppressNextAddClickRef.current) {
        suppressNextAddClickRef.current = false
        stopAddingElementEvent(event)
        return
      }

      onAddAnnotation(event)
    },
    [onAddAnnotation, stopAddingElementEvent],
  )

  useEffect(() => {
    const annotationsCanvas = annotationsCanvasRef.current

    if (isAddingElement && annotationsCanvas) {
      if (isAddingFreehandAnnotation) {
        window.addEventListener('pointerdown', onFreehandPointerDown, true)
        window.addEventListener('pointermove', onFreehandPointerMove, true)
        window.addEventListener('pointerup', onFreehandPointerUp, true)
        window.addEventListener('pointercancel', onFreehandPointerCancel, true)
      } else {
        window.addEventListener('pointerdown', onAddAnnotationPointerDown, true)
        annotationsCanvas.addEventListener('click', onAddAnnotationClick, true)
      }

      annotationsCanvas.style.cursor = placementCursor
    } else if (!isAddingElement && annotationsCanvas) {
      window.removeEventListener(
        'pointerdown',
        onAddAnnotationPointerDown,
        true,
      )
      annotationsCanvas.removeEventListener('click', onAddAnnotationClick, true)
      window.removeEventListener('pointerdown', onFreehandPointerDown, true)
      window.removeEventListener('pointermove', onFreehandPointerMove, true)
      window.removeEventListener('pointerup', onFreehandPointerUp, true)
      window.removeEventListener('pointercancel', onFreehandPointerCancel, true)
      suppressNextAddClickRef.current = false
      stopFreehandDrawing()
    }

    return () => {
      if (annotationsCanvas) {
        window.removeEventListener(
          'pointerdown',
          onAddAnnotationPointerDown,
          true,
        )
        annotationsCanvas.removeEventListener(
          'click',
          onAddAnnotationClick,
          true,
        )
        window.removeEventListener('pointerdown', onFreehandPointerDown, true)
        window.removeEventListener('pointermove', onFreehandPointerMove, true)
        window.removeEventListener('pointerup', onFreehandPointerUp, true)
        window.removeEventListener(
          'pointercancel',
          onFreehandPointerCancel,
          true,
        )
        annotationsCanvas.style.cursor = 'auto'
      }
    }
  }, [
    isAddingElement,
    isAddingFreehandAnnotation,
    annotationsCanvasRef,
    newElementData,
    type,
    placementCursor,
    onAddAnnotationClick,
    onAddAnnotationPointerDown,
    onFreehandPointerCancel,
    onFreehandPointerDown,
    onFreehandPointerMove,
    onFreehandPointerUp,
    stopFreehandDrawing,
  ])

  // Custom MouseSensor that only activates on left-click (button 0)
  class LeftClickMouseSensor extends MouseSensor {
    static activators = [
      {
        eventName: 'onMouseDown' as const,
        handler: ({ nativeEvent: event }: { nativeEvent: MouseEvent }) => {
          /**
           * Invariant: when a modal dialog is open, draggable sensors must stay
           * inactive so document/template drag cannot start behind the modal.
           */
          if (document.querySelector('[role="dialog"]')) {
            return false
          }

          return event.button === 0
        },
      },
    ]
  }

  const mouseSensor = useSensor(LeftClickMouseSensor, {
    activationConstraint: {
      distance: 3,
    },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 0,
      tolerance: 5,
    },
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  const showContainerBorder = useMemo(
    () =>
      (isAddingElement &&
        newElementData?.documentPageId === documentPage?.id) ||
      isDragging ||
      Boolean(draggingElementId),
    [
      isAddingElement,
      newElementData,
      documentPage,
      isDragging,
      draggingElementId,
    ],
  )

  const snapToGrid = () => {
    if (showGrid) {
      return createSnapModifier({
        gridDimensions: {
          height: GRID_HEIGHT,
          width: GRID_WIDTH,
        },
        getGridContainer: () => gridRef.current,
        getZoom: () => zoomPercentage,
      })
    }

    return createSnapModifier({
      gridDimensions: {
        height: 1,
        width: 1,
      },
      getGridContainer: () => gridRef.current,
      getZoom: () => zoomPercentage,
    })
  }

  useEffect(() => {
    if (!templateAttributes?.length || !bulkSelectedTemplateAttributes.length) {
      return
    }

    const refreshedSelection = bulkSelectedTemplateAttributes.map(
      (selectedTemplateAttribute) =>
        templateAttributes.find((t) => t.id === selectedTemplateAttribute.id) ??
        selectedTemplateAttribute,
    )

    const didSelectionChange = refreshedSelection.some(
      (templateAttribute, index) =>
        templateAttribute !== bulkSelectedTemplateAttributes[index],
    )

    if (didSelectionChange) {
      setDocumentState({
        bulkSelectedTemplateAttributes: refreshedSelection,
      })
    }
  }, [bulkSelectedTemplateAttributes, setDocumentState, templateAttributes])

  const moveTemplateAttributeByDocumentDelta = useCallback(
    ({
      delta,
      template,
    }: {
      delta: Position
      template: TemplateAttribute<any>
    }) => {
      return updateTemplate(
        getTemplateAttributeMoveUpdate({
          delta,
          documentPage,
          template,
        }),
      )
    },
    [documentPage, updateTemplate],
  )

  const moveTemplateAttributeByScreenDelta = useCallback(
    ({
      delta,
      template,
    }: {
      delta: Position
      template: TemplateAttribute<any>
    }) => {
      return moveTemplateAttributeByDocumentDelta({
        delta: {
          x: delta.x / zoomPercentage,
          y: delta.y / zoomPercentage,
        },
        template,
      })
    },
    [moveTemplateAttributeByDocumentDelta, zoomPercentage],
  )

  const updateKonvaAnnotationPosition = useCallback(
    ({
      annotation,
      position,
      template,
    }: {
      annotation: KonvaAnnotationTemplate
      position: { x: number; y: number }
      template: TemplateAttribute<any>
    }) => {
      const delta = getKonvaAnnotationDragDelta({
        annotation,
        documentPage,
        position,
        template,
      })

      if (!delta.x && !delta.y) {
        return Promise.resolve()
      }

      const updatePromises: Array<Promise<unknown> | undefined> = [
        moveTemplateAttributeByDocumentDelta({
          delta,
          template,
        }),
      ]

      bulkSelectedTemplateAttributes.forEach((selectedTemplateAttribute) => {
        if (selectedTemplateAttribute.id === template.id) {
          return
        }

        const currentSelectedTemplateAttribute =
          templateAttributes?.find(
            (currentTemplateAttribute) =>
              currentTemplateAttribute.id === selectedTemplateAttribute.id,
          ) ?? selectedTemplateAttribute
        updatePromises.push(
          moveTemplateAttributeByDocumentDelta({
            delta,
            template: currentSelectedTemplateAttribute,
          }),
        )
      })

      return Promise.all(updatePromises).then(() => undefined)
    },
    [
      bulkSelectedTemplateAttributes,
      documentPage,
      moveTemplateAttributeByDocumentDelta,
      templateAttributes,
    ],
  )

  const updateKonvaAnnotationGeometry = useCallback(
    ({
      annotation,
      template,
    }: {
      annotation: KonvaAnnotationTemplate
      template: TemplateAttribute<any>
    }) => {
      const templateValues = template.template_values || {}

      if (isKonvaAnnotationTemplate(templateValues)) {
        return updateTemplate({
          data_type: template.data_type,
          templateAttributeId: template.id as string,
          payload: {
            ...templateValues,
            geometry: annotation.geometry,
          },
        })
      }

      // Legacy annotations can lose edited geometry when saved back through
      // type-specific legacy fields. Persist the edited item as native Konva.
      return updateTemplate({
        data_type: template.data_type,
        templateAttributeId: template.id as string,
        payload: annotation,
      })
    },
    [updateTemplate],
  )

  const onKonvaAnnotationSelect = useCallback(
    (template: TemplateAttribute<any>) => {
      setSelectedElement({
        documentPageId: documentPage.id as string,
        id: template.id as string,
        isRemovable: true,
        type: template.data_type,
      })
    },
    [documentPage.id, setSelectedElement],
  )

  const onKonvaAnnotationColorChange = useCallback(
    async ({
      color,
      template,
    }: {
      color: string
      template: TemplateAttribute<any>
    }) => {
      const values = template.template_values || {}

      if (!isKonvaAnnotationTemplate(values)) {
        await updateTemplate({
          data_type: template.data_type,
          templateAttributeId: template.id as string,
          payload: {
            strokeColor: color,
          },
        })
        return
      }

      await updateTemplate({
        data_type: template.data_type,
        templateAttributeId: template.id as string,
        payload: {
          style: {
            ...values.style,
            stroke: color,
          },
        },
      })
    },
    [updateTemplate],
  )

  const onKonvaAnnotationTextChange = useCallback(
    async ({
      text,
      template,
    }: {
      text: string
      template: TemplateAttribute<any>
    }) => {
      const values = template.template_values || {}

      if (!isKonvaAnnotationTemplate(values)) {
        await updateTemplate({
          data_type: template.data_type,
          templateAttributeId: template.id as string,
          payload: {
            text,
          },
        })
        return
      }

      await updateTemplate({
        data_type: template.data_type,
        templateAttributeId: template.id as string,
        payload: {
          text: {
            ...values.text,
            value: text,
          },
        },
      })
    },
    [updateTemplate],
  )

  const flushPendingMultiSelectDelta = useCallback(() => {
    multiSelectDeltaFrameRef.current = null

    const pendingDelta = pendingMultiSelectDeltaRef.current
    pendingMultiSelectDeltaRef.current = null

    if (pendingDelta) {
      setMultiSelectDelta(pendingDelta)
    }
  }, [setMultiSelectDelta])

  const flushPendingKonvaAnnotationDragPreview = useCallback(() => {
    konvaAnnotationDragPreviewFrameRef.current = null

    const pendingPreview = pendingKonvaAnnotationDragPreviewRef.current
    pendingKonvaAnnotationDragPreviewRef.current = null

    if (pendingPreview) {
      dispatchKonvaAnnotationDragPreview(pendingPreview)
    }
  }, [])

  const cancelPendingMultiSelectDelta = useCallback(() => {
    if (multiSelectDeltaFrameRef.current !== null) {
      cancelAnimationFrame(multiSelectDeltaFrameRef.current)
      multiSelectDeltaFrameRef.current = null
    }

    pendingMultiSelectDeltaRef.current = null
  }, [])

  const cancelPendingKonvaAnnotationDragPreview = useCallback(() => {
    if (konvaAnnotationDragPreviewFrameRef.current !== null) {
      cancelAnimationFrame(konvaAnnotationDragPreviewFrameRef.current)
      konvaAnnotationDragPreviewFrameRef.current = null
    }

    pendingKonvaAnnotationDragPreviewRef.current = null
  }, [])

  const scheduleMultiSelectDelta = useCallback(
    (delta: { x: number; y: number }) => {
      pendingMultiSelectDeltaRef.current = delta

      if (multiSelectDeltaFrameRef.current === null) {
        multiSelectDeltaFrameRef.current = requestAnimationFrame(
          flushPendingMultiSelectDelta,
        )
      }
    },
    [flushPendingMultiSelectDelta],
  )

  const scheduleKonvaAnnotationDragPreview = useCallback(
    (preview: KonvaAnnotationDragPreviewDetail) => {
      pendingKonvaAnnotationDragPreviewRef.current = preview

      if (konvaAnnotationDragPreviewFrameRef.current === null) {
        konvaAnnotationDragPreviewFrameRef.current = requestAnimationFrame(
          flushPendingKonvaAnnotationDragPreview,
        )
      }
    },
    [flushPendingKonvaAnnotationDragPreview],
  )

  useEffect(
    () => () => {
      cancelPendingMultiSelectDelta()
      cancelPendingKonvaAnnotationDragPreview()
    },
    [cancelPendingKonvaAnnotationDragPreview, cancelPendingMultiSelectDelta],
  )

  const onDragEnd = useCallback(
    async ({ delta: { x, y }, active }) => {
      setShowGrid(false)
      /**
       * Invariant:
       * If dnd-kit reports a non-zero drag delta, we should commit it.
       *
       * Why:
       * Rounding with `Math.floor` drops sub-pixel deltas and can leave local
       * draggable state out of sync with server/cache position (the element
       * appears shifted until a larger move is made).
       */
      const hasDelta = Math.abs(x) > 0 || Math.abs(y) > 0
      const currentTemplate = templateAttributes?.find(
        (template) => template.id === active.id,
      )
      const hasAnnotationDragPreview =
        hasDelta &&
        (currentTemplate?.data_type === 'annotation' ||
          bulkSelectedTemplateAttributes.some(
            (template) => template.data_type === 'annotation',
          ))

      if (hasDelta) {
        if (currentTemplate) {
          void moveTemplateAttributeByScreenDelta({
            delta: { x, y },
            template: currentTemplate,
          })
        }

        if (bulkSelectedTemplateAttributes.length > 0) {
          bulkSelectedTemplateAttributes.forEach((bulkTemplateAttribute) => {
            if (bulkTemplateAttribute.id === currentTemplate?.id) {
              return
            }

            const followerTemplateAttribute = templateAttributes?.find(
              (template) => template.id === bulkTemplateAttribute.id,
            )

            if (!followerTemplateAttribute) {
              return
            }

            void moveTemplateAttributeByScreenDelta({
              delta: { x, y },
              template: followerTemplateAttribute,
            })
          })
        }
      }
      setIsDragging(false)
      cancelPendingMultiSelectDelta()
      cancelPendingKonvaAnnotationDragPreview()
      if (hasAnnotationDragPreview) {
        setMultiSelectDelta({ x, y })
      } else {
        setMultiSelectDelta({ x: 0, y: 0 })
      }
      setDraggingElementId(null)

      if (hasAnnotationDragPreview) {
        multiSelectDeltaFrameRef.current = requestAnimationFrame(() => {
          multiSelectDeltaFrameRef.current = null
          setMultiSelectDelta({ x: 0, y: 0 })
        })
      }
    },
    [
      bulkSelectedTemplateAttributes,
      cancelPendingKonvaAnnotationDragPreview,
      cancelPendingMultiSelectDelta,
      moveTemplateAttributeByScreenDelta,
      setMultiSelectDelta,
      setDraggingElementId,
      templateAttributes,
    ],
  )

  if (
    !docData ||
    !docData.project ||
    !docData.version ||
    !docData.documentPages
  ) {
    return null
  }

  return (
    <AnnotationsProvider
      setIsResizing={setIsDragging}
      annotationsCanvasRef={annotationsCanvasRef}
      floatingOverlayElement={floatingOverlayElement}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[snapToGrid()]}
        onDragStart={({ active }) => {
          cancelPendingMultiSelectDelta()
          cancelPendingKonvaAnnotationDragPreview()
          setMultiSelectDelta({ x: 0, y: 0 })
          setIsDragging(true)
          const draggableId = active.id as string
          const dataType = active.data.current?.type as TemplateAttributeTypes

          setDraggingElementId(draggableId)
          setSelectedElement(null)
          setSelectedElement({
            documentPageId: documentPage.id as string,
            id: draggableId,
            isRemovable: true,
            type: dataType,
          })
        }}
        onDragMove={({ active, delta }) => {
          const { type } = (active.data?.current as any) || {}

          const hasFollowerSelection = bulkSelectedTemplateAttributes.some(
            (template) => template.id !== (active.id as string),
          )
          const hasActiveBulkSelection = bulkSelectedTemplateAttributes.some(
            (template) => template.id === (active.id as string),
          )
          if (
            hasFollowerSelection ||
            (type === 'annotation' && hasActiveBulkSelection)
          ) {
            scheduleMultiSelectDelta(delta)
          } else if (type === 'annotation') {
            // Single annotation drags only need to move the Konva preview.
            // Keep that off the global store so pointer frames stay cheap.
            scheduleKonvaAnnotationDragPreview({
              delta,
              draggingId: active.id as string,
            })
          }

          if (type !== 'annotation') {
            setShowGrid(true)
          }
        }}
        onDragEnd={onDragEnd}
        autoScroll={false}
        onDragCancel={() => {
          setIsDragging(false)
          cancelPendingMultiSelectDelta()
          cancelPendingKonvaAnnotationDragPreview()
          setMultiSelectDelta({ x: 0, y: 0 })
          setDraggingElementId(null)
        }}
      >
        <div
          className={cn(
            'flex flex-col rounded border flex-1 mb-9 relative justify-between',
            {
              'border-transparent': !showContainerBorder,
              'border-solid': showContainerBorder,
              'border-primary-40': showContainerBorder,
              'shadow-md': showContainerBorder,
              'shadow-gray-400': showContainerBorder,
            },
          )}
          ref={annotationsCanvasRef}
          id={`droppable-container--${documentPage.id}${
            printSubPageId ? `--${printSubPageId}` : ''
          }`}
          data-document-placement-cursor-locked={
            isAddingElement ? 'true' : undefined
          }
          style={
            {
              '--document-placement-cursor': placementCursor,
            } as React.CSSProperties
          }
        >
          <DocumentGrid
            gridDimensions={{
              width: GRID_WIDTH,
              height: GRID_HEIGHT,
            }}
            showGrid={showGrid}
            ref={gridRef}
          />
          {visibleKonvaAnnotationStackBands.map((band) => (
            <KonvaAnnotationsLayer
              key={band.id}
              annotationLayerActions={annotationLayerActions}
              containerRef={annotationsCanvasRef}
              documentPage={documentPage}
              floatingOverlayElement={floatingOverlayElement}
              isInteractive={!isReadOnly && !isAddingElement}
              multiSelectedElementIds={multiSelectedElementIds}
              selectedElementId={selectedAnnotationId}
              stackZIndex={band.z}
              templateAttributes={band.templateAttributes}
              onAnnotationDragStateChange={setIsDragging}
              onAnnotationSelect={onKonvaAnnotationSelect}
              onAnnotationDragEnd={updateKonvaAnnotationPosition}
              onAnnotationResizeEnd={updateKonvaAnnotationGeometry}
              onAnnotationColorChange={onKonvaAnnotationColorChange}
              onAnnotationTextChange={onKonvaAnnotationTextChange}
              onClearSelection={() => setSelectedElement(null)}
            />
          ))}
          {freehandDrawingStroke && (
            <svg className="pointer-events-none absolute inset-0 z-[9999] overflow-visible">
              <polyline
                points={freehandDrawingStroke.points
                  .map((point) => `${point.x},${point.y}`)
                  .join(' ')}
                fill="none"
                stroke={freehandDrawingStroke.stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={freehandDrawingStroke.strokeWidth}
              />
            </svg>
          )}
          {isAddingElement &&
            newElementData?.documentPageId === documentPage.id &&
            isLine(type || '') &&
            newElementData?.attributes.startPoint && (
              <div className="pointer-events-none absolute z-[9999]">
                <Line
                  startPoint={newElementData?.attributes.startPoint}
                  endPoint={
                    newElementData?.attributes.endPoint ||
                    newElementData?.attributes.startPoint
                  }
                  strokeOpacity={canDropElement ? 0.6 : 1}
                  strokeColor={
                    canDropElement
                      ? newElementData?.attributes.strokeColor ||
                        DEFAULT_STROKE_COLOR
                      : DISABLED_STROKE_COLOR
                  }
                  isArrow={isArrow(type || '')}
                  isBubble={(type && type?.includes('bubble')) || false}
                  bubbleValue={newElementData?.attributes.bubbleValue}
                  renderWrapper={true}
                  textPosition={type?.split('--')[1] || ''}
                />
              </div>
            )}
          <DraggableElements
            isReadOnly={isReadOnly}
            documentPage={documentPage}
            fullSubPageIds={fullSubPageIds}
            hiddenSubPageIds={hiddenSubPageIds}
            annotationsContainerRef={
              annotationsCanvasRef as React.RefObject<HTMLDivElement>
            }
            onNudgeStart={(showGrid: boolean) => {
              if (showGrid) {
                setShowGrid(true)
              }
              setIsDragging(true)
            }}
            onNudgeEnd={(hideGrid: boolean) => {
              if (hideGrid) {
                setShowGrid(false)
              }
              setIsDragging(false)
            }}
          />
          {children}
          <div
            ref={setFloatingOverlayElement}
            className="pointer-events-none absolute inset-0 z-[10000] overflow-visible"
            data-document-floating-overlay
          />
        </div>
      </DndContext>
    </AnnotationsProvider>
  )
}
